import config from "../config";

const appsScriptUrl = config.appscript_url;

// Retry wrapper for API calls with exponential backoff
async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 500
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      if (error?.status >= 400 && error?.status < 500) {
        throw error;
      }
      if (attempt < maxRetries - 1) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  throw lastError;
}

interface OrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  imageUrl?: string;
}

interface OrderData {
  customerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  items: OrderItem[];
  totalAmount: number;
  shippingFee?: number;
  paymentStatus: string;
  orderStatus?: string;
  mayaRRN?: string;
  hostUrl?: string;
}

interface Order {
  orderId: string;
  customerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  items: OrderItem[];
  totalAmount: number;
  shippingFee?: number;
  paymentStatus: string;
  orderStatus: string;
  mayaRRN: string;
  createdDate: string;
  updatedDate: string;
}

interface OrdersResponse {
  success: boolean;
  orders?: Order[];
  stats?: {
    total: number;
    page: number;
    pages: number;
  };
  error?: string;
}

interface OrderResponse {
  success: boolean;
  order?: Order;
  orderId?: string;
  message?: string;
  error?: string;
}

export interface ProductData {
  name: string;
  price: number;
  imageUrl: string;
  notes: string;
  shippingCost: number;
  cartUrl: string;
  sku?: string;           // Product SKU (auto-generated, optional to customize)
  stock: number;          // Stock quantity (required)
  stockVisible: boolean;  // Whether to show stock to customers (required)
  allowCOD?: boolean;     // Whether to allow Cash on Delivery (COD)
}

export interface GeneratedProduct extends ProductData {
  productId: string;
  createdDate: string;
  sku: string;            // SKU is always present (generated or custom)
}

interface ProductsResponse {
  success: boolean;
  products?: GeneratedProduct[];
  stats?: {
    total: number;
    page: number;
    pages: number;
  };
  error?: string;
}

interface ProductResponse {
  success: boolean;
  productId?: string;
  product?: GeneratedProduct;
  message?: string;
  error?: string;
}

// Helper function to normalize Order data
const normalizeOrder = (order: any): Order => {
  // If totalAmount is corrupted (contains a string status), try to recover it
  let totalAmount = Number(order.totalAmount) || 0;
  
  // If totalAmount is NaN or looks like a status string, use subtotal as fallback
  if (isNaN(totalAmount) || (typeof order.totalAmount === 'string' && ['paid', 'pending', 'processing', 'completed', 'shipped', 'delivered', 'cancelled'].includes(order.totalAmount?.toLowerCase()))) {
    console.warn(`totalAmount corrupted (${order.totalAmount}), using subtotal (${order.subtotal}) as fallback`);
    totalAmount = Number(order.subtotal) || 0;
  }
  
  // If shippingFee is corrupted (contains a string status), set to 0
  let shippingFee = Number(order.shippingFee) || 0;
  if (typeof order.shippingFee === 'string' && isNaN(Number(order.shippingFee))) {
    console.warn(`shippingFee corrupted (${order.shippingFee}), setting to 0`);
    shippingFee = 0;
  }
  
  return {
    ...order,
    totalAmount,
    shippingFee,
    items: (order.items || []).map((item: any) => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      totalPrice: Number(item.totalPrice) || 0,
    })),
  };
};



const orderApi = {
  createOrder: async (orderData: OrderData): Promise<OrderResponse> => {
    return fetchWithRetry(async () => {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
        },
        body: JSON.stringify({
          action: "createOrder",
          ...orderData,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      if (!data.success) {
        throw new Error(data.error || "Failed to create order");
      }
      return data;
    }, 3, 500).catch((error: any) => {
      console.error("Error creating order:", error);
      return { success: false, error: error.message || "Failed to create order" };
    });
  },

  getOrder: async (orderId: string): Promise<OrderResponse> => {
    try {
      const response = await fetch(
        `${appsScriptUrl}?action=getOrder&orderId=${encodeURIComponent(orderId)}`,
        {
          method: "GET",
        }
      );
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      // Normalize order data to ensure numeric fields are numbers
      if (data.success && data.order) {
        data.order = normalizeOrder(data.order);
      }
      
      return data;
    } catch (error) {
      console.error("Error getting order:", error);
      return { success: false, error: "Failed to get order" };
    }
  },

  updateOrderStatus: async (
    orderId: string,
    status: string
  ): Promise<OrderResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "updateOrderStatus",
          orderId,
          status,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error updating order status:", error);
      return { success: false, error: "Failed to update order status" };
    }
  },

  updatePaymentStatus: async (
    orderId: string,
    status: string
  ): Promise<OrderResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "updatePaymentStatus",
          orderId,
          status,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error updating payment status:", error);
      return { success: false, error: "Failed to update payment status" };
    }
  },

  getOrders: async (
    page: number = 1,
    limit: number = 20,
    searchQuery: string = "",
    statusFilter: string = "",
    paymentStatusFilter: string = "",
    dateFilter: string = ""
  ): Promise<OrdersResponse> => {
    try {
      const params = new URLSearchParams({
        action: "getOrders",
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (searchQuery) {
        params.append("searchQuery", searchQuery);
      }
      if (statusFilter) {
        params.append("statusFilter", statusFilter);
      }
      if (paymentStatusFilter) {
        params.append("paymentStatusFilter", paymentStatusFilter);
      }
      if (dateFilter) {
        params.append("dateFilter", dateFilter);
      }
      
      const url = `${appsScriptUrl}?${params}`;
      
      const response = await fetch(url, {
        method: "GET",
      });
      
      const text = await response.text();
      
      const data = JSON.parse(text);
      
      // Normalize order data to ensure numeric fields are numbers
      if (data.success && data.orders) {
        data.orders = data.orders.map((order: any) => normalizeOrder(order));
      }
      
      return data;
    } catch (error) {
      console.error("Error getting orders:", error);
      return { success: false, error: "Failed to get orders" };
    }
  },

  createProduct: async (productData: ProductData): Promise<ProductResponse> => {
    try {
      // Generate productId on frontend so we can use it immediately
      const productId = "PROD-" + Date.now().toString(36).toUpperCase();
      
      const params = new URLSearchParams({
        action: "createProduct",
        productId: productId,
        name: productData.name || "",
        price: productData.price?.toString() || "0",
        imageUrl: productData.imageUrl || "",
        notes: productData.notes || "",
        shippingCost: productData.shippingCost?.toString() || "0",
        sku: productData.sku || "",
        stock: productData.stock?.toString() || "0",
        stockVisible: productData.stockVisible ? "true" : "false",
        allowCOD: productData.allowCOD ? "true" : "false",
      });
      
      const url = `${appsScriptUrl}?${params}`;
      
      const response = await fetch(url, { method: "GET" });
      const text = await response.text();
      
      const result = JSON.parse(text);
      
      if (result.success) {
        return { success: true, productId: productId, message: "Product created" };
      }
      return { success: false, error: result.error || "Failed to create product" };
    } catch (error) {
      console.error("Error creating product:", error);
      return { success: false, error: "Failed to create product" };
    }
  },

  getProduct: async (productId: string): Promise<ProductResponse> => {
    try {
      const params = new URLSearchParams({
        action: "getProduct",
        productId: productId,
      });

      const url = `${appsScriptUrl}?${params}`;
      const response = await fetch(url, {
        method: "GET",
      });

      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      console.error("Error getting product:", error);
      return { success: false, error: "Failed to get product" };
    }
  },

  updateProductCartUrl: async (productId: string, cartUrl: string): Promise<ProductResponse> => {
    try {
      const params = new URLSearchParams({
        action: "updateProductCartUrl",
        productId: productId,
        cartUrl: cartUrl,
      });

      const url = `${appsScriptUrl}?${params}`;
      
      const response = await fetch(url, { method: "GET" });
      const text = await response.text();
      
      const result = JSON.parse(text);
      return result;
    } catch (error) {
      console.error("Error updating product cart URL:", error);
      return { success: false, error: "Failed to update cart URL" };
    }
  },

  updateProduct: async (productId: string, updates: { name?: string; price?: number; imageUrl?: string; notes?: string; shippingCost?: number; stock?: number; sku?: string; stockVisible?: boolean; allowCOD?: boolean }): Promise<ProductResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "updateProduct",
          productId: productId,
          updates: JSON.stringify(updates),
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error updating product:", error);
      return { success: false, error: "Failed to update product" };
    }
  },

  getProducts: async (
    page: number = 1,
    limit: number = 20
  ): Promise<ProductsResponse> => {
    try {
      const params = new URLSearchParams({
        action: "getProducts",
        page: page.toString(),
        limit: limit.toString(),
      });

      const url = `${appsScriptUrl}?${params}`;
      const response = await fetch(url, {
        method: "GET",
      });

      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      console.error("Error getting products:", error);
      return { success: false, error: "Failed to get products" };
    }
  },

  deleteOrder: async (orderId: string): Promise<OrderResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "deleteOrder",
          orderId,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error deleting order:", error);
      return { success: false, error: "Failed to delete order" };
    }
  },

  deleteProduct: async (productId: string): Promise<ProductResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "deleteProduct",
          productId,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error deleting product:", error);
      return { success: false, error: "Failed to delete product" };
    }
  },

  sendMessage: async (
    orderId: string,
    email: string,
    customerName: string,
    subject: string,
    message: string
  ): Promise<OrderResponse> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "sendMessage",
          orderId,
          email,
          customerName,
          subject,
          message,
        }),
      });
      
      const text = await response.text();
      const result = JSON.parse(text);
      return result;
    } catch (error) {
      console.error("Error sending message:", error);
      return { success: false, error: "Failed to send message: " + error };
    }
  },

  // Gmail integration
  getGmailThread: async (email: string, orderId: string): Promise<any> => {
    try {
      const params = new URLSearchParams({
        action: "getGmailThread",
        email: email,
        orderId: orderId,
      });
      
      const response = await fetch(`${appsScriptUrl}?${params}`, {
        method: "GET",
      });
      
      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      console.error("Error getting Gmail thread:", error);
      return { success: false, error: "Failed to get email thread" };
    }
  },

  sendCustomerEmail: async (
    email: string,
    orderId: string,
    customerName: string,
    subject: string,
    message: string
  ): Promise<any> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "sendCustomerEmail",
          email,
          orderId,
          customerName,
          subject,
          message,
        }),
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error sending customer email:", error);
      return { success: false, error: "Failed to send email" };
    }
  },

  // Update product stock after purchase
  updateStock: async (productId: string, quantity: number, transactionId: string): Promise<any> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({
          action: "updateStock",
          productId,
          quantityToDeduct: quantity,
          transactionId,
        }),
      });

      const text = await response.text();
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error("Error updating stock:", error);
      return { success: false, error: "Failed to update stock" };
    }
  },

  // Get Settings (includes dynamic quota when API key provided)
  getSettings: async (): Promise<any> => {
    try {
      const response = await fetch(appsScriptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Authorization": `Bearer ${config.api_key}`,
        },
        body: JSON.stringify({ action: "getSettings" }),
      });
      
      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      console.error("Error getting settings:", error);
      return { success: false, error: "Failed to get settings" };
    }
  },

  // Get Customers
  getCustomers: async (): Promise<any> => {
    try {
      const params = new URLSearchParams({ action: "getCustomers" });
      const url = `${appsScriptUrl}?${params}`;
      
      const response = await fetch(url, { method: "GET" });
      const text = await response.text();
      return JSON.parse(text);
    } catch (error) {
      console.error("Error getting customers:", error);
      return { success: false, error: "Failed to get customers" };
    }
  },
};
export type { OrderData, Order, OrderResponse, OrdersResponse };

export default orderApi;
